import { clearSegment } from '../navigation/geometry.js';
import { cliffObstacles } from './cliffs.js';
import { caveWindow, terrainHeight, terrainLatticeHeight, terrainMesh } from './terrain.js';
import type { MapDefinition, Obstacle, Point } from './world-types.js';

/**
 * Caves under roofs (`MapTerrain.roofs`, `MapDefinition.caves`). Where a roof stands, the ground has two levels: the roof's
 * top, which is the surface (level 0), and the cave's floor beneath (its level, 1 for the first cave). Everywhere else both
 * levels share one ground, so a unit belongs to a level only under a roof: there `UnitState.level` says which of the two it is
 * on, and it can only have got there through the cave's mouths, where the floor runs out from under the roof.
 *
 * Each level has its own view of the map (`levelMap`): the surface is the map itself; a cave's view has its floor under the
 * roof and its own obstacles there (the cave's walls and furniture), with the roof's rim walled off so nothing walks or sees
 * between the cave and the ground over it except through a mouth. Two units meet (block, see and reach each other) on the
 * level they share (`meetingLevel`): a unit outside every roof shares either.
 */

/** Below this the roof only grazes the floor: not yet a cave. */
const ROOF_GAP = .25;
/** A roof's rim stands this close to the top of the rock; a mouth opens lower. */
const RIM_DROP = 3;
/** Rim walls reach far over any viewer, so they hide everything beyond them. */
const RIM_HEIGHT = 1e4;

type CaveGrid = { level: number; i0: number; j0: number; columns: number; rows: number; dx: number; dz: number; width: number; depth: number; under: Uint8Array };
const grids = new WeakMap<object, CaveGrid[]>();
const surfaces = new WeakMap<MapDefinition, MapDefinition>();
const views = new WeakMap<MapDefinition, MapDefinition[]>();

/** The map as the surface sees it, from any of its levels' views. */
export const surfaceMap = (map: MapDefinition) => surfaces.get(map) ?? map;

/** Under each cave level's window, which lattice vertices have that level's floor beneath a roof. */
function caveGrids(map: MapDefinition): CaveGrid[] {
  const surface = surfaceMap(map), terrain = surface.terrain;
  if (!terrain?.roofs?.length || !surface.caves?.length) return [];
  const known = grids.get(terrain);
  if (known) return known;
  const mesh = terrainMesh(surface), found: CaveGrid[] = [];
  surface.caves.forEach((_, index) => {
    const level = index + 1, window = caveWindow(surface, level);
    if (!window) return;
    const floor = { ...surface, level }, columns = window.i1 - window.i0 + 1, rows = window.j1 - window.j0 + 1, under = new Uint8Array(columns * rows);
    for (let j = 0; j < rows; j++) for (let i = 0; i < columns; i++)
      if (terrainLatticeHeight(surface, window.i0 + i, window.j0 + j) - terrainLatticeHeight(floor, window.i0 + i, window.j0 + j) > ROOF_GAP) under[j * columns + i] = 1;
    found.push({ level, i0: window.i0, j0: window.j0, columns, rows, dx: mesh.dx, dz: mesh.dz, width: surface.width, depth: surface.depth, under });
  });
  grids.set(terrain, found);
  return found;
}

/** The cave level whose roof stands over a point, or 0 where nothing roofs the ground. */
export function caveAt(map: MapDefinition, x: number, z: number): number {
  for (const grid of caveGrids(map)) {
    const i = Math.round((x + grid.width / 2) / grid.dx) - grid.i0, j = Math.round((z + grid.depth / 2) / grid.dz) - grid.j0;
    if (i >= 0 && j >= 0 && i < grid.columns && j < grid.rows && grid.under[j * grid.columns + i]) return grid.level;
  }
  return 0;
}

/** The cave level whose roofs' bounds take in a point (a skylight's floor, say), or 0. */
export function caveAround(map: MapDefinition, x: number, z: number): number {
  for (const grid of caveGrids(map)) {
    const i = Math.round((x + grid.width / 2) / grid.dx) - grid.i0, j = Math.round((z + grid.depth / 2) / grid.dz) - grid.j0;
    if (i >= 0 && j >= 0 && i < grid.columns && j < grid.rows) return grid.level;
  }
  return 0;
}

type Walker = Point & { level?: number };
/** The level a unit stands on: its cave's under that cave's roof, otherwise the surface's (which outside roofs is every level's ground). */
export function standingLevel(map: MapDefinition, unit: Walker): number {
  if (!unit.level) return 0;
  return caveAt(map, unit.x, unit.z) === unit.level ? unit.level : 0;
}

/**
 * The level two units share, or undefined when rock lies between them: one on a roof, the other in the cave beneath. A unit
 * outside every roof shares any level, so it meets a unit in a cave on the cave's level (through its mouths only).
 */
export function meetingLevel(map: MapDefinition, a: Walker, b: Walker): number | undefined {
  if (!map.caves?.length) return 0;
  const coveredA = caveAt(map, a.x, a.z), coveredB = caveAt(map, b.x, b.z);
  const levelA = coveredA && a.level === coveredA ? coveredA : 0, levelB = coveredB && b.level === coveredB ? coveredB : 0;
  if (coveredA && coveredB) return levelA === levelB ? levelA : undefined;
  return coveredA ? levelA : coveredB ? levelB : 0;
}

/** Whether rock lies between two units, so they neither block, see nor reach each other. */
export const apart = (map: MapDefinition, a: Walker, b: Walker) => meetingLevel(map, a, b) === undefined;

/** The ground height under a unit on the level it walks. */
export const levelHeight = (map: MapDefinition, x: number, z: number, level = 0) => terrainHeight(levelMap(map, level), x, z);

/**
 * The map as one level sees it: the surface is the map itself. A cave's view has the cave's floor under its roofs, the cliffs
 * of that ground, the surface's other obstacles except those standing on its roofs, the cave's own, and walls along each roof's
 * rim (`rimWalls`). Cached per map.
 */
export function levelMap(map: MapDefinition, level: number): MapDefinition {
  const surface = surfaceMap(map);
  if (!level || !surface.caves?.[level - 1]) return surface;
  let cached = views.get(surface);
  if (!cached) views.set(surface, cached = []);
  if (cached[level]) return cached[level];
  const ground: MapDefinition = { ...surface, level };
  const view: MapDefinition = { ...ground, obstacles: [
    ...cliffObstacles(ground),
    ...surface.obstacles.filter(obstacle => obstacle.kind !== 'cliff' && caveAt(surface, obstacle.x, obstacle.z) !== level),
    ...surface.caves[level - 1].obstacles,
    ...rimWalls(surface, level),
  ] };
  surfaces.set(view, surface);
  cached[level] = view;
  return view;
}

/** The view of the level a unit walks (`UnitState.level`). */
export const walkingMap = (map: MapDefinition, unit: Walker) => levelMap(map, unit.level ?? 0);

/** Puts a unit on a level; by default, a unit placed under a roof stands in the cave. */
export function placeOnLevel(map: MapDefinition, unit: Walker, level = caveAt(map, unit.x, unit.z)) {
  if (level) unit.level = level; else delete unit.level;
}

/** The level a unit starts on: its spawn's, or under a roof the cave's. */
export const spawnLevel = (map: MapDefinition, spawn: Walker) => spawn.level ?? caveAt(map, spawn.x, spawn.z);

/** Before a unit walks on to its next waypoint: on open ground it takes the level that waypoint was planned on (`onLevel`). */
export function followLevel(map: MapDefinition, unit: Walker & { route: (Point & { level?: number })[] }) {
  const next = unit.route[0]?.level;
  if (next !== undefined && next !== (unit.level ?? 0) && !caveAt(map, unit.x, unit.z)) placeOnLevel(map, unit, next);
}

/** Lattice vertices on a roof's edge, each a rim (the rock stands up to the roof's top beside it) or a mouth (the floor runs out). */
function roofEdges(map: MapDefinition, level: number) {
  const grid = caveGrids(map).find(item => item.level === level), rims: number[] = [], mouths: number[] = [];
  if (!grid) return { grid, rims, mouths };
  const surface = surfaceMap(map), { columns, rows, under, i0, j0 } = grid;
  const height = (i: number, j: number) => terrainLatticeHeight(surface, i0 + i, j0 + j);
  for (let j = 1; j < rows - 1; j++) for (let i = 1; i < columns - 1; i++) {
    const at = j * columns + i;
    if (!under[at]) continue;
    const outside = [[1, 0], [-1, 0], [0, 1], [0, -1]].filter(([di, dj]) => !under[(j + dj) * columns + i + di]);
    if (!outside.length) continue;
    // The roof's top here: the highest surface within a metre or so.
    let top = -Infinity;
    for (let dj = -2; dj <= 2; dj++) for (let di = -2; di <= 2; di++) {
      const ni = i + di, nj = j + dj;
      if (ni >= 0 && nj >= 0 && ni < columns && nj < rows) top = Math.max(top, height(ni, nj));
    }
    if (outside.some(([di, dj]) => height(i + di, j + dj) > top - RIM_DROP)) rims.push(at); else mouths.push(at);
  }
  return { grid, rims, mouths };
}

/**
 * Walls along a cave level's roof rims: the rock between the cave and the ground beside its roof, which in the cave's view is
 * otherwise open (the ground there is the surface's). They stop walking and every line of sight; mouths stay open.
 */
function rimWalls(map: MapDefinition, level: number): Obstacle[] {
  const { grid, rims } = roofEdges(map, level);
  if (!grid) return [];
  const rim = new Uint8Array(grid.columns * grid.rows);
  for (const at of rims) rim[at] = 1;
  // Runs along each row, merged with identical runs in the rows below, as cliffs are.
  type Run = { start: number; end: number; first: number };
  const walls: Obstacle[] = [], close = (run: Run, last: number) => {
    const x0 = (grid.i0 + run.start - .5) * grid.dx - grid.width / 2, x1 = (grid.i0 + run.end + .5) * grid.dx - grid.width / 2;
    const z0 = (grid.j0 + run.first - .5) * grid.dz - grid.depth / 2, z1 = (grid.j0 + last + .5) * grid.dz - grid.depth / 2;
    walls.push({ kind: 'cliff', x: (x0 + x1) / 2, z: (z0 + z1) / 2, width: x1 - x0, depth: z1 - z0, height: RIM_HEIGHT, rim: RIM_HEIGHT });
  };
  let open = new Map<string, Run>();
  for (let j = 0; j < grid.rows; j++) {
    const next = new Map<string, Run>();
    for (let i = 0; i < grid.columns; i++) {
      if (!rim[j * grid.columns + i]) continue;
      let end = i;
      while (end + 1 < grid.columns && rim[j * grid.columns + end + 1]) end++;
      const key = `${i}:${end}`, above = open.get(key);
      if (above) open.delete(key);
      next.set(key, above ?? { start: i, end, first: j });
      i = end;
    }
    for (const run of open.values()) close(run, j - 1);
    open = next;
  }
  for (const run of open.values()) close(run, grid.rows - 1);
  return walls;
}

const exitCache = new WeakMap<object, Map<number, Point[]>>();
/**
 * Where a cave level's mouths open: one point for each, a few metres out from under the roof on ground both the surface and the
 * cave can stand on. A route between the roof's top and the cave beneath changes level at one of them.
 */
export function caveExits(map: MapDefinition, level: number, radius = .5): Point[] {
  const surface = surfaceMap(map), terrain = surface.terrain;
  if (!terrain) return [];
  let byLevel = exitCache.get(terrain);
  if (!byLevel) exitCache.set(terrain, byLevel = new Map());
  const known = byLevel.get(level);
  if (known) return known;
  const { grid, mouths } = roofEdges(surface, level), exits: Point[] = [];
  if (grid) {
    const { columns, under } = grid, mouth = new Set(mouths), seen = new Set<number>();
    const cave = levelMap(surface, level);
    for (const first of mouths) {
      if (seen.has(first)) continue;
      // One mouth: its edge vertices joined corner to corner.
      const cluster: number[] = [], stack = [first]; seen.add(first);
      while (stack.length) {
        const at = stack.pop()!; cluster.push(at);
        for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
          const next = at + dj * columns + di;
          if (mouth.has(next) && !seen.has(next)) { seen.add(next); stack.push(next); }
        }
      }
      if (cluster.length < 3) continue;
      let cx = 0, cz = 0, ox = 0, oz = 0;
      for (const at of cluster) {
        const i = at % columns, j = (at - i) / columns;
        cx += i; cz += j;
        for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) if (!under[(j + dj) * columns + i + di]) { ox += di; oz += dj; }
      }
      const length = Math.hypot(ox, oz);
      if (!length) continue;
      const centre = { x: (grid.i0 + cx / cluster.length) * grid.dx - grid.width / 2, z: (grid.j0 + cz / cluster.length) * grid.dz - grid.depth / 2 };
      for (let out = 2; out <= 8; out += .5) {
        const point = { x: centre.x + ox / length * out, z: centre.z + oz / length * out };
        if (caveAt(surface, point.x, point.z)) continue;
        if (clearSegment(surface, point, point, radius) && clearSegment(cave, point, point, radius) && clearSegment(cave, centre, point, radius)) { exits.push(point); break; }
      }
    }
  }
  byLevel.set(level, exits);
  return exits;
}

/** The units that stand in the way on one level: those on it, and those outside every roof, whose ground every level shares. */
export function blockersOn<T extends Walker>(map: MapDefinition, level: number, units: T[]): T[] {
  if (!map.caves?.length) return units;
  return units.filter(unit => { const covered = caveAt(map, unit.x, unit.z); return !covered || (unit.level === covered ? covered : 0) === level; });
}

/**
 * The level a unit's route to `target` runs on, without changing level on the way: the level it stands on under a roof,
 * otherwise the level of the target under its roof, otherwise the level it already walks. `targetLevel` is the target's level
 * when it is a unit or a point picked on a level; a bare point under a roof is taken to be in the cave.
 */
export function routeLevel(map: MapDefinition, unit: Walker, target: Point, targetLevel?: number): number {
  if (!map.caves?.length) return 0;
  const covered = caveAt(map, unit.x, unit.z);
  if (covered) return unit.level === covered ? covered : 0;
  const under = caveAt(map, target.x, target.z);
  if (under) return targetLevel === undefined ? under : targetLevel === under ? under : 0;
  return unit.level ?? 0;
}
